import type { BreakCardState } from '../../app/coach';
import type { ManageRow } from '../../app/halftime';
import type { MatchPurpose } from '../../types';

/**
 * B-14 — the break hub's **pure decisions** (design spec §2.4 break-hub arm; §10 F-2; scenarios
 * 20/21). DOM-free so the Node suite drives every arm (§12's grammar); the surface component is
 * glue over these plus the engine's own halftime facade (`loadHalftimeContext` /
 * `commitHalftimeNote` / `swapBoardTile` / `addHalftimeHypothesis` — reached through A's
 * `useHalftime`, imported there, never re-derived here).
 */

/**
 * F-2 — **the purpose fork** (scenarios 20/21): a team match offers the adjustments workflow at
 * halftime; an advance scout's halftime is observation-only — no workflow entry anywhere.
 *
 * `null` falls on the team-match side, deliberately. A halftime match with no purpose can only
 * arrive from imported data (B's own setup gates Begin on the choice — B-7), and both rulings
 * point the same way: F-2's backfill is `'teamMatch'` ("every A match ran the team-facing
 * workflow"), and the engine's own guard rejects exactly `advanceScout` + halftime
 * (`ERR_CARD_CONTEXT`, `commands/coachCard.ts`) — so offering the entry to a `null`-purpose
 * match can never let a card be created that the engine would refuse.
 */
export function offersAdjustmentsWorkflowB(purpose: MatchPurpose | null): boolean {
  return purpose !== 'advanceScout';
}

/**
 * What a "put this pattern on the board" tap should do next — DD-3's two paths, decided from
 * the same rows the list renders:
 *
 * - a board with room → `fill`: dispatch `swapBoardTile(incoming, null)` directly ("no sheet
 *   appears" — an empty slot needs no displacement choice);
 * - a full board → `choose`: show the displacement picker over the positioned tiles (scenario
 *   12: promoting onto a full set requires an explicit user choice of what it displaces).
 *
 * This decides only **which affordance to show**. The engine's handler remains the authority on
 * legality — it re-reads positions inside the transaction, and a stale render that chose `fill`
 * against a board that has since filled is *rejected* by the handler (`SwapBoardTile`: a `null`
 * outgoing on a full board is a rejection, never a silent choice), surfacing as a notice rather
 * than a silently displaced tile.
 */
export type SwapPlanB =
  | { readonly kind: 'fill' }
  | { readonly kind: 'choose'; readonly outgoing: readonly ManageRow[] };

/** The board's tiles in position order — the displacement picker's list and the plan's input. */
export function positionedRowsB(rows: readonly ManageRow[]): readonly ManageRow[] {
  return rows
    .filter((row) => row.boardPosition !== null)
    .sort((a, b) => (a.boardPosition ?? 0) - (b.boardPosition ?? 0));
}

export function swapPlanB(rows: readonly ManageRow[], boardCap: number): SwapPlanB {
  const positioned = positionedRowsB(rows);
  if (positioned.length < boardCap) return { kind: 'fill' };
  return { kind: 'choose', outgoing: positioned };
}

// ── B-31 / BH-2 — the entry card says what survives (design spec §21.3) ───────

/** The entry card's two lines. */
export interface BreakEntryCopyB {
  readonly title: string;
  readonly note: string;
}

/**
 * §21.3 — **the entry names its state.** Treat this as at least as important as hoisting the
 * open flag: on 2026-08-17 the stakeholder's rows survived a navigation intact and he still
 * concluded the work was gone, because this card was pure create-copy in all three states and the
 * hub imports no `app/coach`, so it could not say otherwise. *A silent survival is not a survival.*
 *
 * The count differs by state on purpose (see {@link BreakCardState}): a draft reports what has
 * been **accepted** — decisions the user made — while a finished card reports what it actually
 * **renders**, which includes the adjustments he wrote himself.
 *
 * Total over the three states, so a fourth card status would be a compile error rather than a
 * silent fall-through to the create copy.
 */
export function breakEntryCopyB(card: BreakCardState): BreakEntryCopyB {
  switch (card.status) {
    case 'draft':
      return {
        title: 'Halftime adjustments — in progress',
        note: `${countPhraseB(card.accepted, 'accepted')} so far. Pick up where you left off.`,
      };
    case 'final':
      return {
        title: 'Halftime adjustments — card made',
        note: `${countPhraseB(card.renderable, 'adjustment')}. Open it to read them out.`,
      };
    case 'none':
      return {
        title: 'Build halftime adjustments',
        note: 'Optional — turn what you marked into a talking plan for the second half. Skipping costs nothing.',
      };
  }
}

/**
 * `3 accepted` / `1 adjustment` / `5 adjustments`. English pluralisation of a bare count, kept
 * here rather than inline so the two sub-lines cannot disagree about it — the `accepted` form is
 * an adjective and never takes the `s`, which is exactly the kind of detail that drifts when two
 * template strings each grow their own ternary.
 */
function countPhraseB(count: number, noun: 'accepted' | 'adjustment'): string {
  if (noun === 'accepted') return `${String(count)} accepted`;
  return `${String(count)} adjustment${count === 1 ? '' : 's'}`;
}
